import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  Modal,
  RefreshControl,
} from 'react-native';
import { useFocusEffect, useRouter, useLocalSearchParams } from 'expo-router';
import { teamsApi, TeamMember } from '../../../src/api/client';
import { useEncryptionStore } from '../../../src/stores/encryptionStore';
import { useAuthStore } from '../../../src/stores/authStore';
import { generateTeamKey, wrapTeamKey } from '../../../src/utils/teamCrypto';
import { resolveTeamName, SHARED_TEAM_FALLBACK } from '../../../src/utils/teamName';
import { arrayBufferToBase64, base64ToUint8Array } from '../../../src/utils/encoding';
import { getErrorMessage } from '../../../src/utils/errors';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { aesGcmEncrypt, getRandomBytes } from '../../../src/utils/crypto';
import ScreenHeader from '../../../src/components/ScreenHeader';

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.bgSecondary,
  },
  centered: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 24,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: theme.colors.textMuted,
  },
  teamInfoBar: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  teamName: {
    fontSize: 16,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
  },
  memberCount: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: theme.colors.primaryBg,
  },
  memberCountText: {
    fontSize: 13,
    fontWeight: '600' as const,
    color: theme.colors.primary,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700' as const,
    color: theme.colors.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  memberRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.surface,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: 15,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  memberMeta: {
    fontSize: 13,
    color: theme.colors.textMuted,
  },
  ownerBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: theme.colors.warningBg,
    marginLeft: 8,
  },
  ownerBadgeText: {
    fontSize: 11,
    fontWeight: '600' as const,
    color: theme.colors.warningText,
    textTransform: 'uppercase' as const,
  },
  transferBtn: {
    padding: 8,
  },
  transferBtnText: {
    fontSize: 13,
    color: theme.colors.primary,
    fontWeight: '600' as const,
  },
  removeBtn: {
    padding: 8,
  },
  removeBtnText: {
    fontSize: 13,
    color: theme.colors.error,
    fontWeight: '600' as const,
  },
  actionSection: {
    marginTop: 24,
    backgroundColor: theme.colors.surface,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  actionRow: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    alignItems: 'center' as const,
  },
  actionText: {
    fontSize: 15,
    color: theme.colors.primary,
    fontWeight: '500' as const,
  },
  destructiveText: {
    fontSize: 15,
    color: theme.colors.error,
    fontWeight: '500' as const,
  },
  emptyText: {
    fontSize: 15,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
  },
  errorText: {
    fontSize: 15,
    color: theme.colors.error,
    textAlign: 'center' as const,
  },
  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center' as const,
    paddingHorizontal: 24,
  },
  modalContent: {
    backgroundColor: theme.colors.background,
    borderRadius: 16,
    padding: 24,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    marginBottom: 16,
  },
  modalInput: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: theme.colors.textPrimary,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  modalActions: {
    flexDirection: 'row' as const,
    justifyContent: 'flex-end' as const,
    marginTop: 16,
    gap: 12,
  },
  modalCancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  modalCancelText: {
    fontSize: 15,
    color: theme.colors.textMuted,
  },
  modalSubmitBtn: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  modalSubmitDisabled: {
    opacity: 0.5,
  },
  modalSubmitText: {
    fontSize: 15,
    color: '#fff',
    fontWeight: '600' as const,
  },
});

export default function TeamDetailScreen() {
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const router = useRouter();
  const { teamId } = useLocalSearchParams<{ teamId: string }>();

  const currentUserId = useAuthStore((s) => s.user?.id);

  const [teamName, setTeamName] = useState<string>(SHARED_TEAM_FALLBACK);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [keyVersion, setKeyVersion] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (!teamId) return;
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    setError(null);

    try {
      const [teamRes, membersRes] = await Promise.all([
        teamsApi.getTeam(teamId),
        teamsApi.getTeamMembers(teamId),
      ]);

      const team = teamRes.data;
      // Reassigned if a rotation below re-encrypts the name under a new key
      let latestTeam = team;
      setKeyVersion(team.current_key_version);
      setMembers(membersRes.data.members);

      // Determine if current user is the owner
      const self = membersRes.data.members.find(
        (m: TeamMember) => m.user_id === currentUserId
      );
      const ownerFlag = self?.role === 'owner';
      setIsOwner(ownerFlag);

      // Auto-detect pending key rotation (member departed via reset/deletion)
      if (ownerFlag) {
        try {
          const keysRes = await teamsApi.getTeamKeys(teamId);
          const ownerKeys = keysRes.data.keys;
          if (ownerKeys.length > 0) {
            const maxWrappedVersion = Math.max(...ownerKeys.map((k: { key_version: number }) => k.key_version));
            if (team.current_key_version > maxWrappedVersion) {
              // Decrypt team name first (needed for re-encryption during rotation)
              const currentName = await resolveTeamName(team);
              await rotateTeamKey(teamId, currentName);
              showAlert('Key Rotated', 'A member was removed. Team key has been rotated for security.');
              // Re-fetch to reflect updated state
              const [updatedTeam, updatedMembers] = await Promise.all([
                teamsApi.getTeam(teamId),
                teamsApi.getTeamMembers(teamId),
              ]);
              latestTeam = updatedTeam.data;
              setKeyVersion(updatedTeam.data.current_key_version);
              setMembers(updatedMembers.data.members);
            }
          }
        } catch {
          // Non-fatal — rotation detection can fail silently
        }
      }

      setTeamName(await resolveTeamName(latestTeam));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load team.'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [teamId, currentUserId]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  const handleInviteMember = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !teamId) return;
    if (!requireConnectivity()) return;

    setIsInviting(true);
    try {
      // 1. Add member — server returns their public key
      const addResponse = await teamsApi.addMember(teamId, email);
      const { public_key } = addResponse.data;

      // 2. Get the current team key from memory
      const encStore = useEncryptionStore.getState();
      const teamKey = encStore.getTeamKey(teamId);
      if (!teamKey || !(teamKey instanceof Uint8Array)) {
        throw new Error('Team key not found. Please try syncing your keys.');
      }

      // 3. Wrap the team key for the invitee using their public key
      const recipientPublicKey = base64ToUint8Array(public_key);
      const wrappedKey = wrapTeamKey(teamKey, recipientPublicKey);
      const wrappedKeyB64 = arrayBufferToBase64(wrappedKey);

      // 4. Upload the wrapped key for the invitee
      await teamsApi.uploadKey(teamId, {
        key_version: keyVersion,
        wrapped_key: wrappedKeyB64,
      });

      setShowInviteModal(false);
      setInviteEmail('');
      fetchData();
      showAlert('Success', `${email} has been added to the team.`);
    } catch (err) {
      showAlert('Error', getErrorMessage(err, 'Failed to invite member.'));
    } finally {
      setIsInviting(false);
    }
  };

  /**
   * Generate a new team AES key, wrap for all remaining members, upload,
   * re-encrypt team name, and update local state.
   */
  const rotateTeamKey = async (tid: string, currentName: string): Promise<void> => {
    // Generate new team key
    const newTeamKey = generateTeamKey();

    // Get team to find the new key version
    const teamRes = await teamsApi.getTeam(tid);
    const newKeyVersion = teamRes.data.current_key_version;

    // Fetch public keys for ALL remaining members
    const pubKeysRes = await teamsApi.getMemberPublicKeys(tid);
    const memberPublicKeys = pubKeysRes.data.keys;

    // Wrap new key for each remaining member
    const wrappedKeys = memberPublicKeys.map((mk) => ({
      user_id: mk.user_id,
      wrapped_key: arrayBufferToBase64(
        wrapTeamKey(newTeamKey, base64ToUint8Array(mk.public_key))
      ),
    }));

    // Upload all wrapped keys
    await teamsApi.rotateKeys(tid, {
      key_version: newKeyVersion,
      wrapped_keys: wrappedKeys,
    });

    // Update local team key
    const encStore = useEncryptionStore.getState();
    encStore.setTeamKey(tid, newTeamKey);

    // Re-encrypt team name with new key
    if (currentName !== SHARED_TEAM_FALLBACK) {
      const iv = getRandomBytes(12);
      const encoder = new TextEncoder();
      const encrypted = await aesGcmEncrypt(newTeamKey, iv, encoder.encode(currentName));
      await teamsApi.updateTeam(tid, {
        name_encrypted: arrayBufferToBase64(encrypted),
        encryption_metadata: { iv: arrayBufferToBase64(iv), alg: 'AES-256-GCM' },
      });
    }

    setKeyVersion(newKeyVersion);
  };

  const handleRemoveMember = (member: TeamMember) => {
    if (!requireConnectivity()) return;
    showConfirm(
      'Remove Member',
      `Remove ${member.email || member.display_name || 'this member'} from the team? They will lose access to all team content.`,
      async () => {
        try {
          // Remove member — server creates new key epoch
          await teamsApi.removeMember(teamId!, member.user_id);

          // Rotate team key for remaining members
          await rotateTeamKey(teamId!, teamName);

          fetchData();
          showAlert('Done', 'Member removed and team key rotated.');
        } catch (err) {
          showAlert('Error', getErrorMessage(err, 'Failed to remove member.'));
        }
      },
      { confirmText: 'Remove', destructive: true }
    );
  };

  const handleTransferOwnership = (member: TeamMember) => {
    if (!requireConnectivity()) return;
    showConfirm(
      'Transfer Ownership',
      `Transfer ownership to ${member.display_name || member.email || 'this member'}? You will become a regular member.`,
      async () => {
        try {
          await teamsApi.transferOwnership(teamId!, member.user_id);
          fetchData();
          showAlert('Done', 'Ownership transferred successfully.');
        } catch (err) {
          showAlert('Error', getErrorMessage(err, 'Failed to transfer ownership.'));
        }
      },
      { confirmText: 'Transfer', destructive: true }
    );
  };

  const handleDeleteTeam = () => {
    if (!requireConnectivity()) return;
    showConfirm(
      'Delete Team',
      'This will permanently delete this team. All shared content will become inaccessible. This cannot be undone.',
      () => {
        showConfirm(
          'Confirm Delete',
          'Are you absolutely sure?',
          async () => {
            try {
              await teamsApi.deleteTeam(teamId!);
              useEncryptionStore.getState().setTeamKey(teamId!, null as any);
              router.back();
            } catch (err) {
              showAlert('Error', getErrorMessage(err, 'Failed to delete team.'));
            }
          },
          { confirmText: 'Delete', destructive: true }
        );
      },
      { confirmText: 'Delete', destructive: true }
    );
  };

  const headerView = (
    <ScreenHeader
      title="Team Details"
      left={{
        label: 'Go back',
        icon: 'arrow-back',
        onPress: () => router.back(),
        testID: 'team-detail.back-button',
      }}
      right={
        isOwner
          ? {
              label: 'Invite Member',
              text: '+ Invite',
              tone: 'accent',
              onPress: () => {
                setInviteEmail('');
                setShowInviteModal(true);
              },
              testID: 'team-detail.invite-button',
            }
          : undefined
      }
    />  );

  if (isLoading) {
    return (
      <View style={styles.container}>
        {headerView}
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading team...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        {headerView}
        <View style={styles.centered}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      </View>
    );
  }

  const renderMember = ({ item }: { item: TeamMember }) => {
    const isSelf = item.user_id === currentUserId;
    const isItemOwner = item.role === 'owner';

    return (
      <View
        style={styles.memberRow}
        accessibilityLabel={`${item.email || item.display_name || 'Member'}, ${item.role}`}
        accessibilityRole="button"
        testID={`team-detail.member-${item.user_id}`}
      >
        <View style={styles.memberInfo} accessible={false}>
          <Text style={styles.memberName} accessible={false}>
            {item.display_name || item.email || 'Member'}
            {isSelf ? ' (You)' : ''}
          </Text>
          <Text style={styles.memberMeta} accessible={false}>
            {item.email || `Joined ${new Date(item.joined_at).toLocaleDateString()}`}
          </Text>
        </View>
        {isItemOwner && (
          <View style={styles.ownerBadge} accessible={false}>
            <Text style={styles.ownerBadgeText} accessible={false}>Owner</Text>
          </View>
        )}
        {isOwner && !isSelf && !isItemOwner && (
          <View style={{ flexDirection: 'row', gap: 8 }} accessible={false}>
            <TouchableOpacity
              onPress={() => handleTransferOwnership(item)}
              style={styles.transferBtn}
              accessibilityLabel={`Transfer ownership to ${item.email || item.display_name || 'member'}`}
              accessibilityRole="button"
              testID="team-detail.transfer-ownership-button"
            >
              <Text style={styles.transferBtnText} accessible={false}>Transfer</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => handleRemoveMember(item)}
              style={styles.removeBtn}
              accessibilityLabel={`Remove ${item.email || item.display_name || 'member'}`}
              accessibilityRole="button"
              testID="team-detail.remove-member-button"
            >
              <Text style={styles.removeBtnText} accessible={false}>Remove</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {headerView}

      {/* Team info */}
      <View
        style={styles.teamInfoBar}
        accessibilityLabel={`${teamName}, ${members.length} members`}
        testID="team-detail.team-info"
      >
        <Text style={styles.teamName} accessible={false}>{teamName}</Text>
        <View style={styles.memberCount} accessible={false}>
          <Text style={styles.memberCountText} accessible={false}>
            {members.length} member{members.length !== 1 ? 's' : ''}
          </Text>
        </View>
      </View>

      <Text style={styles.sectionTitle}>Members</Text>

      <FlatList
        data={members}
        keyExtractor={(item) => item.id}
        renderItem={renderMember}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={() => fetchData(true)} />
        }
        ListEmptyComponent={
          <View style={styles.centered}>
            <Text style={styles.emptyText}>No members</Text>
          </View>
        }
        ListFooterComponent={
          <View style={styles.actionSection}>
            {isOwner && (
              <TouchableOpacity
                style={styles.actionRow}
                onPress={handleDeleteTeam}
                accessibilityLabel="Delete Team"
                accessibilityRole="button"
                testID="team-detail.delete-team-button"
              >
                <Text style={styles.destructiveText} accessible={false}>Delete Team</Text>
              </TouchableOpacity>
            )}
          </View>
        }
        testID="team-detail.member-list"
      />

      {/* Invite Modal */}
      <Modal
        visible={showInviteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowInviteModal(false)}
        accessibilityViewIsModal={true}
      >
        <View style={styles.modalOverlay} testID="team-detail.invite-modal">
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle} accessibilityRole="header">Invite Member</Text>
            <Text style={styles.modalSubtitle}>
              Enter the email address of the person you want to invite.
              They must have an account with encryption set up.
            </Text>
            <TextInput
              style={styles.modalInput}
              placeholder="user@example.com"
              value={inviteEmail}
              onChangeText={setInviteEmail}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              autoFocus
              returnKeyType="done"
              onSubmitEditing={handleInviteMember}
              accessibilityLabel="Email address"
              testID="team-detail.invite-email-input"
            />

            <View style={styles.modalActions}>
              <TouchableOpacity
                onPress={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                }}
                style={styles.modalCancelBtn}
                accessibilityLabel="Cancel"
                accessibilityRole="button"
                testID="team-detail.invite-cancel-button"
              >
                <Text style={styles.modalCancelText} accessible={false}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleInviteMember}
                style={[styles.modalSubmitBtn, (isInviting || !inviteEmail.trim()) && styles.modalSubmitDisabled]}
                disabled={isInviting || !inviteEmail.trim()}
                accessibilityLabel="Send Invite"
                accessibilityRole="button"
                testID="team-detail.invite-submit-button"
              >
                {isInviting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.modalSubmitText} accessible={false}>Invite</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
